import { useMutation, useQuery } from '@tanstack/react-query';
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    MutationFunction,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult
} from '@tanstack/react-query';

import { useCallback } from 'react';

import type {
    ArticleGroupPlacement,
    GetProjectsResponse,
    LoadProjectResponse,
    ProblemDetails,
    SaveProjectParams,
    SaveProjectResponse
} from '../../models';

import { useCustomAppClient } from '../../custom-client';
import type { ErrorType } from '../../custom-client';

/**
 * Get all saved projects ordered newest-first
 */
export const useGetProjectsHook = () => {
    const getProjects = useCustomAppClient<GetProjectsResponse>();

    return useCallback(
        (signal?: AbortSignal) => {
            return getProjects({ url: `/projects`, method: 'GET', signal });
        },
        [getProjects]
    );
};

export const getGetProjectsQueryKey = () => {
    return [`/projects`] as const;
};

export const useGetProjectsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>,
    TError = ErrorType<ProblemDetails>
>(options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>, TError, TData>>;
}) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetProjectsQueryKey();

    const getProjects = useGetProjectsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>> = ({ signal }) =>
        getProjects(signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetProjectsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>>;
export type GetProjectsQueryError = ErrorType<ProblemDetails>;

export function useGetProjects<
    TData = Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProjects<
    TData = Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProjects<
    TData = Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetProjects<
    TData = Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetProjectsQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    query.queryKey = queryOptions.queryKey;

    return query;
}

/**
 * Save the current session as a named project. The BuildingModel is read from the session cache — no IFC re-upload required.
 */
export const useSaveProjectHook = () => {
    const saveProject = useCustomAppClient<SaveProjectResponse>();

    return useCallback(
        (articleGroupPlacement: ArticleGroupPlacement[], params: SaveProjectParams, signal?: AbortSignal) => {
            return saveProject({
                url: `/projects`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: articleGroupPlacement,
                params,
                signal
            });
        },
        [saveProject]
    );
};

export const useSaveProjectMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useSaveProjectHook>>>,
        TError,
        { data: ArticleGroupPlacement[]; params: SaveProjectParams },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useSaveProjectHook>>>,
    TError,
    { data: ArticleGroupPlacement[]; params: SaveProjectParams },
    TContext
> => {
    const mutationKey = ['saveProject'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const saveProject = useSaveProjectHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useSaveProjectHook>>>,
        { data: ArticleGroupPlacement[]; params: SaveProjectParams }
    > = (props) => {
        const { data, params } = props ?? {};

        return saveProject(data, params);
    };

    return { mutationFn, ...mutationOptions };
};

export type SaveProjectMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useSaveProjectHook>>>>;
export type SaveProjectMutationBody = ArticleGroupPlacement[];
export type SaveProjectMutationError = ErrorType<ProblemDetails>;

export const useSaveProject = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useSaveProjectHook>>>,
            TError,
            { data: ArticleGroupPlacement[]; params: SaveProjectParams },
            TContext
        >;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useSaveProjectHook>>>,
    TError,
    { data: ArticleGroupPlacement[]; params: SaveProjectParams },
    TContext
> => {
    const mutationOptions = useSaveProjectMutationOptions(options);

    return useMutation(mutationOptions, queryClient);
};
/**
 * Load a saved project into a forming session. Returns the session ID and article groups so the frontend can join the session and replay placements.
 */
export const useLoadProjectHook = () => {
    const loadProject = useCustomAppClient<LoadProjectResponse>();

    return useCallback(
        (projectId: string, signal?: AbortSignal) => {
            return loadProject({ url: `/projects/${projectId}/load`, method: 'POST', signal });
        },
        [loadProject]
    );
};

export const useLoadProjectMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useLoadProjectHook>>>,
        TError,
        { projectId: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useLoadProjectHook>>>,
    TError,
    { projectId: string },
    TContext
> => {
    const mutationKey = ['loadProject'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const loadProject = useLoadProjectHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useLoadProjectHook>>>,
        { projectId: string }
    > = (props) => {
        const { projectId } = props ?? {};

        return loadProject(projectId);
    };

    return { mutationFn, ...mutationOptions };
};

export type LoadProjectMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useLoadProjectHook>>>>;

export type LoadProjectMutationError = ErrorType<ProblemDetails>;

export const useLoadProject = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useLoadProjectHook>>>,
            TError,
            { projectId: string },
            TContext
        >;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useLoadProjectHook>>>,
    TError,
    { projectId: string },
    TContext
> => {
    const mutationOptions = useLoadProjectMutationOptions(options);

    return useMutation(mutationOptions, queryClient);
};
/**
 * Permanently delete a saved project
 */
export const useDeleteProjectHook = () => {
    const deleteProject = useCustomAppClient<void>();

    return useCallback(
        (projectId: string) => {
            return deleteProject({ url: `/projects/${projectId}`, method: 'DELETE' });
        },
        [deleteProject]
    );
};

export const useDeleteProjectMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteProjectHook>>>,
        TError,
        { projectId: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteProjectHook>>>,
    TError,
    { projectId: string },
    TContext
> => {
    const mutationKey = ['deleteProject'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const deleteProject = useDeleteProjectHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteProjectHook>>>,
        { projectId: string }
    > = (props) => {
        const { projectId } = props ?? {};

        return deleteProject(projectId);
    };

    return { mutationFn, ...mutationOptions };
};

export type DeleteProjectMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDeleteProjectHook>>>>;

export type DeleteProjectMutationError = ErrorType<ProblemDetails>;

export const useDeleteProject = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteProjectHook>>>,
            TError,
            { projectId: string },
            TContext
        >;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteProjectHook>>>,
    TError,
    { projectId: string },
    TContext
> => {
    const mutationOptions = useDeleteProjectMutationOptions(options);

    return useMutation(mutationOptions, queryClient);
};
